import {
  type Gradient,
  type GradientBGVarProps,
  GridItem,
  LayoutGrid,
  RadialGradientBG,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import type { SourceHTMLAttributes } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';
import type { SVGIconComponent } from 'types/svgr';

import type { ImageProps } from '@/app/components/Image';

import SectionContent from './SectionContent';
import type { SectionContentAppUIProps } from './SectionContentAppUI';
import SectionMedia from './SectionMedia';
import Time from './Time';

type SectionVariant = 'dark' | 'light';

export interface SectionProps {
  // the index of the currently active section
  readonly activeIndex: number;
  // image fallback for video and for motion reduce
  readonly altImage: ImageProps;
  // props for the primary app UI graphic displayed with the section content
  readonly appUi: Omit<
    SectionContentAppUIProps,
    'activeIndex' | 'index' | 'sectionCount'
  >;
  // background color for the section
  readonly backgroundColor: GradientBGVarProps['background'];
  // content of the section
  readonly content: MessageKey;
  // gradients for the background
  readonly gradients: Gradient[];
  // heading of the section
  readonly heading: MessageKey;
  // icon for the time
  readonly Icon: SVGIconComponent;
  // unique id for the section
  readonly id: string;
  // zero-based index of the section on the timeline
  readonly index: number;
  // the index of the previously active section
  readonly previousIndex: number;
  // total number of sections within the timeline
  readonly sectionCount: number;
  // time of the day
  readonly time: MessageKey;
  // variant of the section for typography
  readonly variant: {
    sm: SectionVariant;
    lg: SectionVariant;
  };
  // video src set for the background video
  readonly videoSrcSet: SourceHTMLAttributes<HTMLSourceElement>[];
}

const Section = ({
  activeIndex,
  altImage,
  appUi,
  backgroundColor,
  content,
  gradients,
  heading,
  Icon,
  id,
  index,
  previousIndex,
  sectionCount,
  time,
  variant,
  videoSrcSet,
}: SectionProps): JSX.Element => {
  const isActive = activeIndex === index;

  return (
    <div
      className="lg:js:pointer-events-none lg:js:absolute lg:js:z-10 lg:js:size-full"
      data-cy="timeline_section_wrapper"
    >
      <section
        className={cx(
          'relative lg:left-0 lg:top-0 lg:h-screen lg:w-screen lg:js:sticky',
          {
            'text-sandstone-200': variant.sm === 'dark',
            'text-sandstone-500': variant.sm === 'light',
            'lg:text-sandstone-200': variant.lg === 'dark',
            'lg:text-sandstone-500': variant.lg === 'light',
          },
        )}
        data-cy="timeline_section"
      >
        <RadialGradientBG
          background={backgroundColor}
          className={cx(
            'pointer-events-none absolute left-0 top-0 z-0 size-full lg:js:transition-opacity lg:js:duration-500',
            {
              'lg:js:opacity-100': isActive,
              'lg:js:opacity-0 lg:js:delay-500': !isActive,
            },
          )}
          gradients={gradients}
        />
        <div className="relative z-10 lg:static">
          <div className="absolute left-6 top-6 z-10 lg:hidden">
            <Time Icon={Icon} time={time} variant={variant} />
          </div>
          <SectionMedia
            activeIndex={activeIndex}
            altImage={altImage}
            id={id}
            index={index}
            previousIndex={previousIndex}
            sectionCount={sectionCount}
            videoSrcSet={videoSrcSet}
          />
        </div>
        <div className="pb-20 pt-24">
          <LayoutGrid>
            <GridItem
              colEnd={{ sm: 'main', lg: 11 }}
              colStart={{ sm: 'main', lg: 4 }}
            >
              <SectionContent
                activeIndex={activeIndex}
                appUi={appUi}
                content={content}
                heading={heading}
                Icon={Icon}
                index={index}
                time={time}
                variant={variant}
              />
            </GridItem>
          </LayoutGrid>
        </div>
      </section>
    </div>
  );
};

export default Section;
